import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useAuth } from '../context/AuthContext';

function isCreditBillAdjustNote(note) {
  const n = (note || '').toLowerCase();
  return (
    n.includes('[wallet]') ||
    n.includes('credit bill reduced') ||
    n.includes('credit bill adjusted')
  );
}

function isBalanceAfterCouponNote(note) {
  const n = (note || '').toLowerCase();
  return n.includes('credit charged after coupon') || n.includes('balance after coupon');
}

/** Coupon name and sale note shown under customer in lists. */
export default function SaleAnnotations({ sale }) {
  const { couponsEnabled } = useAuth();
  if (!sale) return null;

  const showCoupon = couponsEnabled && !!sale.couponName;
  const note = sale.saleNote || '';
  const billAdjust = couponsEnabled && isCreditBillAdjustNote(note);
  const afterCoupon = couponsEnabled && isBalanceAfterCouponNote(note);
  const amount = Number(sale.totalAmount);
  const isCreditReduction = billAdjust && Number.isFinite(amount) && amount < 0;

  if (!showCoupon && !note) return null;

  return (
    <Box sx={{ mt: 0.25 }}>
      {billAdjust ? (
        <Chip
          size="small"
          icon={<LocalOfferIcon />}
          label={isCreditReduction ? 'Credit bill reduced' : 'Coupon → credit bill'}
          color={isCreditReduction ? 'success' : 'info'}
          variant="outlined"
          sx={{ height: 22, mb: 0.25, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
        />
      ) : null}
      {afterCoupon ? (
        <Chip
          size="small"
          icon={<LocalOfferIcon />}
          label="Credit after coupon"
          color="warning"
          variant="outlined"
          sx={{ height: 22, mb: 0.25, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
        />
      ) : null}
      {showCoupon && !billAdjust ? (
        <Typography variant="caption" display="block" color="text.secondary">
          Coupon: {sale.couponName}
        </Typography>
      ) : null}
      {note ? (
        <Typography variant="caption" display="block" color="text.secondary">
          {note}
        </Typography>
      ) : null}
    </Box>
  );
}

/** Credit remainder after a POS coupon sale covered part of the cart. */
export function balanceAfterCouponNote(couponName, creditTotal, couponTotal) {
  const name = (couponName || '').trim() || 'Coupon';
  const credit = Number(creditTotal);
  const covered = Number(couponTotal);
  if (Number.isFinite(credit) && Number.isFinite(covered) && (credit > 0 || covered > 0)) {
    return `Credit charged Nu ${credit.toFixed(2)} after coupon “${name}” covered Nu ${covered.toFixed(2)}`;
  }
  return `Credit charged after coupon “${name}”`;
}

/** Monthly credit bill reduced by applying a wallet coupon. */
export function creditBillCouponNote(couponName, amount) {
  const name = (couponName || '').trim() || 'Coupon';
  const amt = Number(amount);
  if (Number.isFinite(amt) && amt > 0) {
    return `Credit bill reduced by Nu ${amt.toFixed(2)} using coupon “${name}”`;
  }
  return `Credit bill adjusted with coupon: ${name}`;
}
